import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import {
  PagerLoadMoreVariant,
  PagerPosition,
  PagerVariant
} from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import { LoadingSize } from '@aurora/shared-client/types/enums';
import type { NodeSearchConnection } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import React, { useEffect, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, NodeViewVariant, ViewSize } from '../../../types/enums';
import type {
  NodeSearchQuery,
  NodeSearchQueryVariables,
  NodeSearchViewFragment
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps, ItemViewVariantProps } from '../../entities/types';
import { SearchItemType } from '../../search/enums';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import type { NodeListForSearchTermProps } from '../types';
import localStyles from './NodeListForSearchTerm.module.pcss';
import nodeSearchQuery from './NodeSearch.query.graphql';

/**
 * Displays a list of Nodes(places) for a given search term using nodeSearchQuery
 *
 *  @author Abhijith.Be
 */

const NodeListForSearchTerm: React.FC<React.PropsWithChildren<NodeListForSearchTermProps>> = ({
  onCompleted,
  queryTerm,
  listVariant,
  localizedCategoryId,
  nodeViewVariantPropsOverrides = {},
  pagerVariant = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START,
      variant: PagerLoadMoreVariant.BOXED
    }
  },
  pageSize = 5,
  paneledListClassname,
  headerClassname,
  footerClassname,
  panel = PanelType.NONE,
  useEmpty = false,
  useLoader = true,
  usePrefixSearchOnLastWord = false,
  onRendered,
  constrainPages,
  useHeader
}: NodeListForSearchTermProps) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.NODE_LIST_FOR_SEARCH_TERM
  );
  const currentPageNumber = useRef(0);
  const pageCount = useRef(0);
  const trimmedTerm: string = queryTerm?.trim() ?? '';

  const nodeViewVariantDefaultProps: ItemViewVariantProps<
    ItemType.NODE_SEARCH,
    NodeViewVariant.INLINE
  > = {
    useNodeAvatar: true,
    useNodeDescription: true,
    useChildNodes: false,
    useNodeMembersCount: false,
    useNodeTopicsCount: false,
    nodeIconSize: IconSize.PX_40,
    viewSize: ViewSize.SM
  };

  const nodeViewVariant: ItemViewTypeAndProps<ItemType.NODE_SEARCH, NodeViewVariant.INLINE> = {
    type: NodeViewVariant.INLINE,
    props: {
      ...nodeViewVariantDefaultProps,
      ...nodeViewVariantPropsOverrides
    }
  };

  const rawQueryResult = useEntityViewQuery<
    ItemType.NODE_SEARCH,
    NodeSearchQuery,
    NodeSearchQueryVariables
  >(module, ItemType.NODE_SEARCH, nodeViewVariant, nodeSearchQuery, {
    variables: {
      searchTerm: trimmedTerm,
      ...(localizedCategoryId && {
        constraints: {
          rootCategoryId: {
            eq: `root_category.id:${localizedCategoryId.split(':')[1]}`
          }
        }
      }),
      first: pageSize,
      usePrefixSearchOnLastWord
    },
    onCompleted: data => {
      // Filter nodes with null parent before reporting to parent
      if (data?.nodeSearch?.edges) {
        const filteredEdges = data.nodeSearch.edges.filter(edge => {
          const coreNode = edge.node?.coreNode;
          if (coreNode && 'parent' in coreNode) {
            return coreNode.parent != null;
          }
          return true;
        });

        onCompleted({
          ...data,
          nodeSearch: {
            ...data.nodeSearch,
            edges: filteredEdges,
            totalCount: filteredEdges.length
          }
        });
      } else {
        onCompleted(data);
      }
    },
    onError: React.useCallback(() => {
      onCompleted(null);
    }, [onCompleted]),
    notifyOnNetworkStatusChange: true,
    skip: trimmedTerm.length < 2
  });

  // Filter for display using same logic
  const queryResult = React.useMemo(() => {
    if (!rawQueryResult?.data?.nodeSearch?.edges) {
      return rawQueryResult;
    }

    const filteredEdges = rawQueryResult.data.nodeSearch.edges.filter(edge => {
      const coreNode = edge.node?.coreNode;
      if (coreNode && 'parent' in coreNode) {
        return coreNode.parent != null;
      }
      return true;
    });

    return {
      ...rawQueryResult,
      data: {
        ...rawQueryResult.data,
        nodeSearch: {
          ...rawQueryResult.data.nodeSearch,
          edges: filteredEdges,
          totalCount: filteredEdges.length
        }
      }
    };
  }, [rawQueryResult]);

  useEffect(() => {
    if (onRendered && currentPageNumber.current !== pageCount.current) {
      pageCount.current = currentPageNumber.current;
      onRendered(currentPageNumber.current, SearchItemType.NODE);
    }
  });

  if (textLoading) {
    return null;
  }

  /**
   * Callback after pagination has occurred
   * @param connection
   */
  function onUpdate(connection: NodeSearchConnection): NodeSearchQuery {
    currentPageNumber.current += 1;
    const { pageInfo } = connection;
    const { hasNextPage } = pageInfo;
    return {
      nodeSearch: {
        ...connection,
        pageInfo: {
          ...pageInfo,
          hasNextPage: hasNextPage && currentPageNumber.current < constrainPages
        }
      }
    } as NodeSearchQuery;
  }

  if (pagerVariant) {
    pagerVariant.props = pagerVariant.props || {};
    pagerVariant.props.ariaLabel = formatMessage('showMoreBoards');
  }

  /**
   * call back function when there is no results found
   */
  const NoResults: React.FC<React.PropsWithChildren<unknown>> = () => (
    <EmptyState
      className={cx('lia-g-mt-10')}
      description={formatMessage('noResultTitle')}
      descriptionClassName={cx('lia-empty-state-description')}
      alignment={TextAlignment.LEFT}
    />
  );

  /**
   * The Header to be rendered for the PaneledItemList.
   *
   * @param key the key to fetch the header text for.
   */
  function renderResultsHeader(key: string): React.FC<React.PropsWithChildren<unknown>> {
    function Header(): React.ReactElement {
      return <h5 className={cx('lia-content-heading')}>{formatMessage(key)}</h5>;
    }
    return Header;
  }

  return queryResult?.loading || queryResult?.data?.nodeSearch?.edges?.length > 0 ? (
    <PaneledItemList<
      NodeSearchViewFragment,
      ItemType.NODE_SEARCH,
      ItemViewTypeAndProps<ItemType.NODE_SEARCH, NodeViewVariant>,
      NodeSearchQuery,
      NodeSearchQueryVariables
    >
      className={paneledListClassname}
      type={ItemType.NODE_SEARCH}
      variant={nodeViewVariant}
      queryResult={queryResult}
      listVariant={listVariant}
      pagerVariant={pagerVariant}
      pageSize={pageSize}
      onUpdate={constrainPages && onUpdate}
      loadingSize={useLoader ? LoadingSize.LG : null}
      empty={NoResults}
      header={useHeader ? renderResultsHeader('places') : null}
      panel={panel}
      useHeaderWhenEmpty={false}
      headerClassName={headerClassname}
      footerClassName={footerClassname}
      useEmpty={useEmpty}
    />
  ) : null;
};

export default NodeListForSearchTerm;
